// ---------------------------------------------------------------------------
// Types and transaction-building helpers for the Contract Explorer module.
//
// Catalogue + generator recipes are loaded from:
//   GET /api/showcase/contract_linkage/cl_contracts/
//   GET /api/showcase/contract_linkage/contract_gens/
//   GET /api/showcase/contract_linkage/unmatched_gens/
//
// The AI matching engine links spend transactions to contracts using different
// evidence: PO references, contracted rates & payment terms, invoicing
// patterns within the contract period, or — when the contract terms are
// unclear — a supplier-level period fallback with low confidence.
// The user reviews these links (accept / relink / unlink) and can pull in
// spend the AI missed, including spend hidden behind marketplaces, payment
// providers or employee reimbursements. Decisions and contract edits are kept
// in localStorage (see contractLinkageUtils.ts).
// ---------------------------------------------------------------------------

export type ClContractType =
    | 'Framework agreement'
    | 'License agreement'
    | 'Service contract'
    | 'Project SOW'
    | 'Maintenance contract';

export type ClContractStatus = 'active' | 'expiring' | 'expired';

export type ClContract = {
    id: string;
    name: string;
    supplierId: string;
    supplier: string;
    type: ClContractType;
    status: ClContractStatus;
    startDate: string; // ISO yyyy-mm-dd
    endDate: string;   // ISO yyyy-mm-dd
    contractedValue: number;
    paymentTerms: string;
    scopeSummary: string;
    /** False when the AI could not parse a rate card / clear scope from the document. */
    clearTerms: boolean;

    // ---- Extracted contract metadata (all optional — older fixtures may lack them) ----
    counterpartyEntity?: string | null;
    contractOwner?: string | null;
    ownerDepartment?: string | null;
    signedDate?: string | null;
    renewalType?: string | null;
    renewalNotice?: string | null;
    pricingModel?: string | null;
    priceAdjustment?: string | null;
    terminationForConvenience?: string | null;
    liabilityCap?: string | null;
    governingLaw?: string | null;
    slaSummary?: string | null;
    sourceFile?: string | null;
    extractedAt?: string | null;
    /** 0–100 confidence of the document extraction. */
    extractionConfidence?: number | null;
};

export type ClMatchBasis = 'po_reference' | 'line_terms' | 'date_window' | 'supplier_period';

export type ClSignalWeight = 'strong' | 'medium' | 'weak';

export type ClSignal = {
    label: string;
    detail: string;
    weight: ClSignalWeight;
};

/** How the spend reached the company — direct invoice or an indirect channel. */
export type ClTxChannel = 'marketplace' | 'payment_provider' | 'employee_expense';

export type ClTransaction = {
    id: string;
    supplierId: string;
    supplier: string;
    description: string;
    docNumber: string;
    /** Invoice date, ISO yyyy-mm-dd. */
    date: string;
    /** Posting date in the ERP, a few days after the invoice date. */
    postingDate: string;
    /** Clearing (payment) date; null while the invoice is still open. */
    clearingDate: string | null;
    amount: number;
    entity: string;
    costCenter: string;
    category: string;
    /** Set when the spend came in via an indirect channel (marketplace, payment provider, employee expense). */
    channel?: ClTxChannel;
    /** Contract proposed by the AI; null when no match was found. */
    aiContractId: string | null;
    /** 0–100; 0 when unmatched. */
    confidence: number;
    basis: ClMatchBasis | null;
    signals: ClSignal[];
    /** Other contracts the AI considered, with their score. */
    alternatives: {contractId: string; confidence: number}[];
};

export type ClBasisProfile = {
    basis: ClMatchBasis;
    /** Confidence range [min, max]. */
    conf: [number, number];
    /** Relative share of this contract's transactions. */
    share: number;
};

/** Recipe used to generate AI-matched transactions for a contract. */
export type ClContractGen = {
    contractId: string;
    count: number;
    amount: [number, number];
    category: string;
    descriptions: string[];
    profiles: ClBasisProfile[];
    /** Alternative contract the AI also considered (same supplier). */
    altContractId?: string;
};

/** Recipe used to generate unmatched transactions. */
export type ClUnmatchedGen = {
    supplierId: string;
    supplier: string;
    count: number;
    amount: [number, number];
    category: string;
    descriptions: string[];
    channel?: ClTxChannel;
    /** Restrict to specific window months (e.g. after a contract expired). */
    months?: [number, number][];
    reason: ClSignal[];
};

export const CL_CURRENCY = 'EUR';

/** Analysis window: the last 12 closed months. */
export const CL_PERIOD = {from: 'Jul 2025', to: 'Jun 2026', label: 'Last 12 closed months'};

/** "Today" in showcase time — right after the analysis window closes. */
export const CL_TODAY = '2026-07-15';

/** Contracts ending within this many days of CL_TODAY count as expiring. */
export const CL_EXPIRING_DAYS = 90;

export const CL_SUBTITLE =
    'Everything about each contract in one place: the terms the AI extracted from the document, the spend linked ' +
    'to it, and the transactions that may belong to it. Review the proposed links, pull in spend the AI missed — ' +
    'including purchases hidden behind marketplaces, payment providers or employee expenses — and keep the ' +
    'contract record itself up to date.';

export function buildContractsById(contracts: ClContract[]): ReadonlyMap<string, ClContract> {
    return new Map(contracts.map(c => [c.id, c]));
}

// ---------------------------------------------------------------------------
// Contract lifecycle (derived from dates so it stays consistent with CL_TODAY)
// ---------------------------------------------------------------------------

const dayMs = 24 * 60 * 60 * 1000;

/** Days from CL_TODAY until the contract ends; negative when already expired. */
export function daysUntilExpiry(c: ClContract, today: string = CL_TODAY): number {
    return Math.round((Date.parse(c.endDate) - Date.parse(today)) / dayMs);
}

export function contractRuntimeStatus(c: ClContract, today: string = CL_TODAY): ClContractStatus {
    const days = daysUntilExpiry(c, today);
    if (days < 0) return 'expired';
    if (days <= CL_EXPIRING_DAYS) return 'expiring';
    return 'active';
}

// ---------------------------------------------------------------------------
// Deterministic transaction generation
// ---------------------------------------------------------------------------

/** Deterministic PRNG so the showcase data is stable across reloads. */
function mulberry32(seed: number): () => number {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const ENTITIES = ['Mithra DE', 'Mithra NL', 'Mithra UK', 'Mithra FR'];

/** Cost centres the generator assigns, keyed by spend category. */
const COST_CENTERS: Record<string, string[]> = {
    'IT Services': ['CC-4100 IT Applications', 'CC-4200 IT Infrastructure'],
    'Software': ['CC-4100 IT Applications', 'CC-4300 Digital Workplace'],
    'IT Hardware & Peripherals': ['CC-4200 IT Infrastructure', 'CC-4300 Digital Workplace'],
    'Contingent Labour': ['CC-2300 Engineering', 'CC-3100 Production', 'CC-1400 HR'],
    'Recruitment': ['CC-1400 HR'],
    'Consulting': ['CC-1010 Transformation Office', 'CC-1300 Procurement'],
    'Logistics': ['CC-3300 Supply Chain', 'CC-3400 Distribution'],
    'Facility Management': ['CC-3600 Facilities'],
    'Maintenance': ['CC-3600 Facilities', 'CC-3100 Production'],
    'Waste Management': ['CC-3600 Facilities'],
    'MRO & Supplies': ['CC-3100 Production', 'CC-3200 Plant Maintenance'],
    'Office Services': ['CC-1200 Workplace Services'],
    'Office Supplies': ['CC-1200 Workplace Services'],
    'Travel': ['CC-1100 Commercial', 'CC-1010 Transformation Office'],
    'Employee Reimbursement': ['CC-1200 Workplace Services', 'CC-3600 Facilities', 'CC-4300 Digital Workplace'],
};

const DEFAULT_COST_CENTERS = ['CC-1000 General & Admin'];

/** Months of the analysis window, as [year, month(1-12)]. */
const WINDOW_MONTHS: [number, number][] = [
    [2025, 7], [2025, 8], [2025, 9], [2025, 10], [2025, 11], [2025, 12],
    [2026, 1], [2026, 2], [2026, 3], [2026, 4], [2026, 5], [2026, 6],
];

const iso = (y: number, m: number, d: number) =>
    `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const addDays = (isoDate: string, days: number): string => {
    const d = new Date(Date.parse(isoDate) + days * dayMs);
    return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
};

/** Payment terms string → days until the invoice clears ("Net 60" → 60). */
const termsDays = (paymentTerms: string | undefined): number => {
    const m = paymentTerms?.match(/Net (\d+)/i);
    return m ? Number(m[1]) : 30;
};

/** Months of the window that fall inside the contract validity. */
function contractMonths(c: ClContract): [number, number][] {
    return WINDOW_MONTHS.filter(([y, m]) => {
        const first = iso(y, m, 1);
        const last = iso(y, m, 28);
        return last >= c.startDate && first <= c.endDate;
    });
}

function signalsFor(basis: ClMatchBasis, contract: ClContract, po: string): ClSignal[] {
    switch (basis) {
        case 'po_reference':
            return [
                {label: 'PO reference match', detail: `Invoice references ${po}, issued under this contract.`, weight: 'strong'},
                {label: 'Within contract period', detail: `Invoice date falls inside the contract validity (${contract.startDate} — ${contract.endDate}).`, weight: 'medium'},
                {label: 'Amount plausible', detail: 'The amount is consistent with the invoicing pattern of this contract.', weight: 'medium'},
            ];
        case 'line_terms':
            return [
                {label: 'Contracted rates match', detail: 'Line prices match the rate card / price list in the contract.', weight: 'strong'},
                {label: 'Payment terms match', detail: `Invoice payment terms equal the contract terms (${contract.paymentTerms}).`, weight: 'medium'},
                {label: 'Within contract period', detail: `Invoice date falls inside the contract validity (${contract.startDate} — ${contract.endDate}).`, weight: 'medium'},
            ];
        case 'date_window':
            return [
                {label: 'Within contract period', detail: `Invoice date falls inside the contract validity (${contract.startDate} — ${contract.endDate}).`, weight: 'medium'},
                {label: 'Recurring amount pattern', detail: 'The amount fits the recurring invoicing pattern observed for this contract.', weight: 'medium'},
                {label: 'No PO or line-item evidence', detail: 'The invoice carries no PO reference and line items could not be matched to the contract.', weight: 'weak'},
            ];
        case 'supplier_period':
            return [
                {label: 'Contract terms unclear', detail: 'The rate card / scope could not be parsed from the contract document, so line-level matching was not possible.', weight: 'weak'},
                {label: 'Supplier-level fallback', detail: 'All spend from this supplier inside the contract validity was allocated to this contract.', weight: 'weak'},
                {label: 'Within contract period', detail: `Invoice date falls inside the contract validity (${contract.startDate} — ${contract.endDate}).`, weight: 'medium'},
            ];
    }
}

/** Expand contract + generator recipes into a deterministic transaction set. */
export function buildTransactions(
    contracts: ClContract[],
    contractGens: ClContractGen[],
    unmatchedGens: ClUnmatchedGen[],
): ClTransaction[] {
    const contractsById = buildContractsById(contracts);
    const rng = mulberry32(20260713);
    const txs: ClTransaction[] = [];
    let seq = 1;

    const pick = <T >(arr: T[]): T => arr[Math.floor(rng() * arr.length)];
    const between = (min: number, max: number) => min + rng() * (max - min);

    const pickProfile = (profiles: ClBasisProfile[]): ClBasisProfile => {
        const r = rng();
        let acc = 0;
        for (const p of profiles) {
            acc += p.share;
            if (r <= acc) return p;
        }
        return profiles[profiles.length - 1];
    };

    /** Posting a few days after the invoice; clearing after the payment terms, unless still open. */
    const lifecycleDates = (invoiceDate: string, paymentTerms?: string) => {
        const postingDate = addDays(invoiceDate, 1 + Math.floor(rng() * 5));
        const cleared = addDays(postingDate, termsDays(paymentTerms) + Math.floor(rng() * 9) - 3);
        return {postingDate, clearingDate: cleared > CL_TODAY ? null : cleared};
    };

    for (const gen of contractGens) {
        const contract = contractsById.get(gen.contractId);
        if (!contract) continue;
        const months = contractMonths(contract);
        if (months.length === 0) continue;
        for (let i = 0; i < gen.count; i++) {
            const profile = pickProfile(gen.profiles);
            const [y, m] = months[i % months.length];
            const day = 1 + Math.floor(rng() * 27);
            const confidence = Math.round(between(profile.conf[0], profile.conf[1]));
            const po = `PO-${45_000 + Math.floor(rng() * 9_000)}`;
            const amount = Math.round(between(gen.amount[0], gen.amount[1]) / 10) * 10;
            const alternatives = gen.altContractId
                ? [{contractId: gen.altContractId, confidence: Math.max(8, confidence - 18 - Math.round(rng() * 20))}]
                : [];
            const date = iso(y, m, day);
            txs.push({
                id: `tx-${String(seq++).padStart(4, '0')}`,
                supplierId: contract.supplierId,
                supplier: contract.supplier,
                // Cycle rather than pick randomly so every description variant appears.
                description: gen.descriptions[i % gen.descriptions.length],
                docNumber: `INV-${y}-${String(10_000 + Math.floor(rng() * 89_000))}`,
                date,
                ...lifecycleDates(date, contract.paymentTerms),
                amount,
                entity: pick(ENTITIES),
                costCenter: pick(COST_CENTERS[gen.category] ?? DEFAULT_COST_CENTERS),
                category: gen.category,
                aiContractId: contract.id,
                confidence,
                basis: profile.basis,
                signals: signalsFor(profile.basis, contract, po),
                alternatives,
            });
        }
    }

    for (const gen of unmatchedGens) {
        const months = gen.months ?? WINDOW_MONTHS;
        for (let i = 0; i < gen.count; i++) {
            const [y, m] = months[i % months.length];
            const day = 1 + Math.floor(rng() * 27);
            const date = iso(y, m, day);
            txs.push({
                id: `tx-${String(seq++).padStart(4, '0')}`,
                supplierId: gen.supplierId,
                supplier: gen.supplier,
                description: gen.descriptions[i % gen.descriptions.length],
                docNumber: `INV-${y}-${String(10_000 + Math.floor(rng() * 89_000))}`,
                date,
                ...lifecycleDates(date),
                amount: Math.round(between(gen.amount[0], gen.amount[1]) / 10) * 10,
                entity: pick(ENTITIES),
                costCenter: pick(COST_CENTERS[gen.category] ?? DEFAULT_COST_CENTERS),
                category: gen.category,
                channel: gen.channel,
                aiContractId: null,
                confidence: 0,
                basis: null,
                signals: gen.reason,
                alternatives: [],
            });
        }
    }

    // Newest first reads naturally in a review queue.
    return txs.sort((a, b) => b.date.localeCompare(a.date));
}

// ---------------------------------------------------------------------------
// Portfolio KPIs (decision-independent; review progress is computed live)
// ---------------------------------------------------------------------------

export type ClKpis = {
    totalSpend: number;
    txCount: number;
    linkedSpend: number;
    linkedCount: number;
    unmatchedSpend: number;
    unmatchedCount: number;
    /** Spend-weighted average confidence over AI-linked transactions. */
    avgConfidence: number;
    contractCount: number;
    supplierCount: number;
};

export function computeClKpis(txs: ClTransaction[], contractCount: number): ClKpis {
    const linked = txs.filter(t => t.aiContractId);
    const unmatched = txs.filter(t => !t.aiContractId);
    const linkedSpend = linked.reduce((s, t) => s + t.amount, 0);
    const confWeighted = linked.reduce((s, t) => s + t.amount * t.confidence, 0);
    return {
        totalSpend: txs.reduce((s, t) => s + t.amount, 0),
        txCount: txs.length,
        linkedSpend,
        linkedCount: linked.length,
        unmatchedSpend: unmatched.reduce((s, t) => s + t.amount, 0),
        unmatchedCount: unmatched.length,
        avgConfidence: linkedSpend > 0 ? confWeighted / linkedSpend : 0,
        contractCount,
        supplierCount: new Set(txs.map(t => t.supplierId)).size,
    };
}

export type ClPortfolioKpis = {
    active: number;
    expiring: number;
    expired: number;
    contractedValue: number;
    /** Unlinked window spend from suppliers whose contract already expired, dated after the expiry. */
    expiredTrailingSpend: number;
};

export function computePortfolioKpis(contracts: ClContract[], txs: ClTransaction[]): ClPortfolioKpis {
    const expired = contracts.filter(c => c.status === 'expired');
    const expiredEndBySupplier = new Map(expired.map(c => [c.supplierId, c.endDate]));
    const expiredTrailingSpend = txs
        .filter(t => !t.aiContractId)
        .filter(t => {
            const end = expiredEndBySupplier.get(t.supplierId);
            return !!end && t.date > end;
        })
        .reduce((s, t) => s + t.amount, 0);
    return {
        active: contracts.filter(c => c.status === 'active').length,
        expiring: contracts.filter(c => c.status === 'expiring').length,
        expired: expired.length,
        contractedValue: contracts.reduce((s, c) => s + c.contractedValue, 0),
        expiredTrailingSpend,
    };
}
